import { API_BASE_URL } from '../config/api';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  buildSuggestedQueryPills,
  type AssistantPageKey,
  type AssistantSuggestedQueryPill,
} from '../constants/assistantSuggestedQueries';
import { notifyVoiceSessionChanged } from '../hooks/useDashboardAssistantContext';
import { buildVoiceSessionOrigin } from '../utils/assistantPageContext';
import { useAssistantViewport } from './useAssistantViewport';
import {
  getAssistantDisplayMode,
  isAssistantDesktopViewport,
  subscribeAssistantViewport,
} from '../utils/assistantDisplayMode';
import { log } from '../utils/logger';
import {
  VoiceMessage,
  VoiceSession,
  migrateLegacyHistory,
  getOrCreateCurrentSession,
  getAllSessions,
  getSessionMessages,
  updateSessionMessages,
  startNewSession,
  loadSession,
  clearAllSessions,
} from '../utils/voiceSessionStorage';
import {
  trackAssistantQuestion,
  clearLoopGuard,
  isLoopDetected,
  getLoopRecoveryMessage,
} from '../utils/voiceLoopGuard';
import { speakResponse, stopSpeaking } from '../utils/voiceTts';
import { preloadVoiceSettings } from '../utils/voiceSettingsStore';
import {
  buildVoiceCommandRequest,
  createVoiceConfirmCard,
  ensureVoiceSessionId,
  extractVoiceQuickPicks,
  getProactiveMessages,
  getVoiceMessageExtras,
  isFeedbackBlocked,
  isIntentFullyExecuted,
  needsContinuation,
  requestServerFastNavigation,
  sendVoiceCommand,
  shouldCloseVoiceAssistant,
  storeSharedMind,
  VoiceCommandResult,
  type VoiceQuickPickOption,
} from '../utils/voiceCommandClient';
import {
  clearPendingContactConfirmation,
  storePendingContactConfirmation,
} from '../utils/voicePendingConfirmation';
import {
  clearPendingDocumentFlow,
  storePendingDocumentFlow,
} from '../utils/voicePendingDocumentFlow';
import { resolveVoiceDetailRoute } from '../utils/voiceDetailNavigation';
import {
  checkQuickNavigation,
  executeNavigation,
  getDestinationFromMessage,
  isNavigationActionCommand,
  resolveContactTypeFilter,
  routeFromDestination,
} from '../utils/voiceQuickNav';
import {
  tryClientNavigationFastTrack,
  tryCreateContactFastTrack,
  tryCreateExpenseFastTrack,
  tryCreateInvoiceFastTrack,
  FastTrackResult,
} from '../utils/voiceFastTrack';
import {
  tryDashboardRefreshFastTrack,
  tryDashboardSuggestedQueryFastTrack,
  dispatchVoiceDashboardAction,
  isDashboardPath,
  maybeCollapseRailOnVoiceNavigate,
  maybeRefreshDashboardAfterMutation,
} from '../utils/voiceDashboardActions';
import { getDashboardProactiveGreeting, isDashboardProactiveGreeting } from '../utils/voiceModeMessages';
import { isMobileDevice } from '../utils/voiceCaptureSupport';
import {
  dispatchVoiceDataRefresh,
  shouldDispatchVoiceDataRefresh,
} from '../utils/voiceDataRefresh';
import {
  extractEstablishedIntent,
  navigateForEstablishedIntent,
} from '../utils/voiceIntentNavigation';
import type { ConfirmCardData } from '../components/VoiceConfirmCard';

type Message = VoiceMessage;

const RAIL_STORAGE_KEY = 'ezacc-assistant-rail';

const readStoredRailOpen = (): boolean | null => {
  try {
    const stored = sessionStorage.getItem(RAIL_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    /* ignore storage errors */
  }
  return null;
};

const persistRailOpen = (open: boolean): void => {
  try {
    sessionStorage.setItem(RAIL_STORAGE_KEY, String(open));
  } catch {
    /* ignore storage errors */
  }
};

const appendRouteNavigation = (route: string): void => {
  const currentRoute = `${window.location.pathname}${window.location.search}`;
  if (currentRoute === route) return;
  window.dispatchEvent(new CustomEvent('voiceNavigation', { detail: { route } }));
};

export const useGlobalVoiceAssistant = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const isDesktop = useAssistantViewport();
  const [railOpen, setRailOpenState] = useState(() => {
    const stored = readStoredRailOpen();
    if (stored !== null && !isAssistantDesktopViewport()) return stored;
    return isAssistantDesktopViewport() || isDashboardPath(window.location.pathname);
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [pastSessions, setPastSessions] = useState<VoiceSession[]>([]);
  const [selectedPastSession, setSelectedPastSession] = useState<VoiceSession | null>(null);
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const [componentData, setComponentData] = useState<any>(null);
  const [confirmCard, setConfirmCard] = useState<ConfirmCardData | null>(null);
  const [quickPicks, setQuickPicks] = useState<VoiceQuickPickOption[]>([]);
  const [dynamicSuggestedQueries, setDynamicSuggestedQueries] = useState<string[]>([]);
  const sessionIdRef = useRef<string>('');
  const pendingDataRefreshRef = useRef<VoiceCommandResult | null>(null);
  const activeIntentRef = useRef<string | null>(null);
  const navigatedForIntentRef = useRef<string | null>(null);
  const dashboardGreetingInjectedRef = useRef(false);

  const pageSuggestedQueries = useMemo(
    () => buildSuggestedQueryPills(
      buildVoiceSessionOrigin(location.pathname, location.search).pageKey as AssistantPageKey,
      t,
      dynamicSuggestedQueries
    ),
    [dynamicSuggestedQueries, location.pathname, location.search, t]
  );

  const sessionOrigin = useCallback(
    () => buildVoiceSessionOrigin(location.pathname, location.search),
    [location.pathname, location.search]
  );

  useEffect(() => {
    migrateLegacyHistory();
    const session = getOrCreateCurrentSession(sessionOrigin());
    sessionIdRef.current = session.id;
    setMessages(session.messages);
    setPastSessions(getAllSessions());
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    setRailOpenState(true);
    persistRailOpen(true);
  }, [isDesktop]);

  useEffect(() => {
    if (isDesktop || readStoredRailOpen() !== null) return;
    setRailOpenState(isDashboardPath(location.pathname));
  }, [isDesktop, location.pathname]);

  useEffect(() => {
    return subscribeAssistantViewport((desktop) => {
      if (desktop) {
        setIsOpen(false);
        setIsMinimized(false);
      }
    });
  }, []);

  const setRailOpen = useCallback((open: boolean) => {
    setRailOpenState(open);
    persistRailOpen(open);
  }, []);

  const toggleRail = useCallback(() => {
    setRailOpenState((prev) => {
      const next = !prev;
      persistRailOpen(next);
      return next;
    });
  }, []);

  const displayMode = getAssistantDisplayMode({ isDesktop, railOpen, isOpen });

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      preloadVoiceSettings().catch(() => undefined);
    }
  }, []);

  const generateMessageId = useCallback(() => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`, []);

  const persistMessages = useCallback((msgs: VoiceMessage[]) => {
    if (!sessionIdRef.current) return;
    updateSessionMessages(sessionIdRef.current, msgs);
    setPastSessions(getAllSessions());
  }, []);

  const toVoiceMessage = useCallback((role: 'user' | 'assistant', content: string, extra?: Partial<VoiceMessage>): VoiceMessage => ({
    id: generateMessageId(),
    role,
    content,
    timestamp: new Date().toISOString(),
    ...extra,
  }), [generateMessageId]);

  const appendAssistantMessage = useCallback((content: string, extra?: Partial<VoiceMessage>) => {
    const message = toVoiceMessage('assistant', content, extra);
    setMessages(prev => [...prev, message]);
    return message;
  }, [toVoiceMessage]);

  useEffect(() => {
    if (messages.length === 0 || !sessionIdRef.current) return;
    const timer = setTimeout(() => persistMessages(messages), 300);
    return () => clearTimeout(timer);
  }, [messages, persistMessages]);

  const pendingAutoStartRef = useRef(false);

  const startVoiceInput = useCallback(() => {
    log.voice.start();
    stopSpeaking();
    setIsRecording(true);
    setIsProcessing(false);
    window.dispatchEvent(new CustomEvent('startVoiceInput'));
  }, []);

  const stopVoiceInput = useCallback(() => {
    window.dispatchEvent(new CustomEvent('stopVoiceInput'));
    setIsRecording(false);
    setCurrentTranscript('');
  }, []);

  useEffect(() => {
    if (!isOpen || isMinimized || isProcessing || !pendingAutoStartRef.current) return;

    pendingAutoStartRef.current = false;
    const timer = window.setTimeout(() => {
      startVoiceInput();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [isOpen, isMinimized, isProcessing, startVoiceInput]);

  const beginNewChatSession = useCallback(() => {
    if (messages.length > 0) persistMessages(messages);
    const session = startNewSession(sessionOrigin());
    sessionIdRef.current = session.id;
    notifyVoiceSessionChanged();
    setMessages([]);
    setCurrentTranscript('');
    setShowHistoryModal(false);
    setConfirmCard(null);
    clearLoopGuard();
    clearPendingContactConfirmation();
    clearPendingDocumentFlow();
    activeIntentRef.current = null;
    navigatedForIntentRef.current = null;
    localStorage.removeItem('voiceSessionId');
    setPastSessions(getAllSessions());
  }, [messages, persistMessages, sessionOrigin]);

  const openAssistant = useCallback(() => {
    beginNewChatSession();

    const desktop = isAssistantDesktopViewport();
    if (desktop) {
      setRailOpen(true);
      setIsMinimized(false);
      return;
    }

    setIsOpen(true);
    setIsMinimized(false);
    // Mobile browsers require a direct tap on the mic button to unlock the microphone.
    pendingAutoStartRef.current = !isMobileDevice();
  }, [beginNewChatSession, setRailOpen]);

  const closeAssistant = useCallback(() => {
    if (messages.length > 0) persistMessages(messages);
    stopSpeaking();
    window.dispatchEvent(new CustomEvent('stopVoiceInput'));
    if (pendingDataRefreshRef.current) {
      dispatchVoiceDataRefresh(pendingDataRefreshRef.current);
      pendingDataRefreshRef.current = null;
    }
    setIsOpen(false);
    setIsMinimized(false);
    setIsRecording(false);
    setCurrentTranscript('');
    setConfirmCard(null);
    setQuickPicks([]);
    setDynamicSuggestedQueries([]);
    activeIntentRef.current = null;
    navigatedForIntentRef.current = null;
    pendingAutoStartRef.current = false;
  }, [messages, persistMessages]);

  const minimizeAssistant = useCallback(() => {
    window.dispatchEvent(new CustomEvent('stopVoiceInput'));
    setIsRecording(false);
    setIsMinimized(true);
  }, []);

  const maximizeAssistant = useCallback(() => {
    setIsMinimized(false);
    pendingAutoStartRef.current = !isMobileDevice();
  }, []);

  const addFastTrackMessage = useCallback((result: FastTrackResult) => {
    if (result.message) appendAssistantMessage(result.message);
    if (result.navigationHref) window.location.href = result.navigationHref;
    if (result.action) {
      const mockResult: VoiceCommandResult = {
        success: true,
        action: result.action,
        message: result.message
      };
      if (shouldDispatchVoiceDataRefresh(mockResult)) {
        pendingDataRefreshRef.current = mockResult;
        dispatchVoiceDataRefresh(mockResult);
        maybeRefreshDashboardAfterMutation(mockResult.action);
      }
    }
  }, [appendAssistantMessage]);

  const tryClientFastTracks = useCallback(async (text: string): Promise<boolean> => {
    const directNav = tryClientNavigationFastTrack(text);
    if (directNav) {
      addFastTrackMessage(directNav);
      return true;
    }

    for (const creator of [tryCreateExpenseFastTrack, tryCreateInvoiceFastTrack, tryCreateContactFastTrack]) {
      const result = await creator(text);
      if (!result) continue;
      addFastTrackMessage(result);
      if (result.handled) return true;
    }

    return false;
  }, [addFastTrackMessage]);

  const handleLocalNavigation = useCallback((text: string): boolean => {
    if (isNavigationActionCommand(text)) return false;
    const navigationResult = checkQuickNavigation(text);
    if (!navigationResult) return false;

    maybeCollapseRailOnVoiceNavigate(navigationResult.destination, setRailOpen);
    executeNavigation(navigationResult.destination);
    const contactType = resolveContactTypeFilter(text);
    if (contactType) {
      window.dispatchEvent(new CustomEvent('voiceContactsAction', { detail: { filterType: contactType } }));
    }
    appendAssistantMessage(navigationResult.response);
    setTimeout(() => {
      setMessages([]);
      closeAssistant();
    }, 100);
    return true;
  }, [appendAssistantMessage, closeAssistant, setRailOpen]);

  const handleServerFastNavigation = useCallback(async (text: string): Promise<boolean> => {
    try {
      const result = await requestServerFastNavigation(text);
      if (!result) return false;
      maybeCollapseRailOnVoiceNavigate(result.data.destination, setRailOpen);
      executeNavigation(result.data.destination);
      appendAssistantMessage(result.message);
      setTimeout(() => {
        setMessages([]);
        closeAssistant();
      }, 1000);
      return true;
    } catch (error) {
      log.debug?.('Fast navigation server call failed, falling back to full processing:', error);
      return false;
    }
  }, [appendAssistantMessage, closeAssistant, setRailOpen]);

  const scheduleProactiveMessages = useCallback((result: VoiceCommandResult) => {
    for (const proactive of getProactiveMessages(result)) {
      setTimeout(() => appendAssistantMessage(proactive.content), proactive.delay);
    }
  }, [appendAssistantMessage]);

  const updateConfirmCard = useCallback((result: VoiceCommandResult) => {
    setConfirmCard(createVoiceConfirmCard(
      result,
      () => {
        setConfirmCard(null);
        window.dispatchEvent(new CustomEvent('voiceConfirmAction', { detail: { text: 'yes confirm' } }));
      },
      () => {
        setConfirmCard(null);
        window.dispatchEvent(new CustomEvent('voiceConfirmAction', { detail: { text: 'cancel' } }));
      }
    ));
  }, []);

  const handleResultNavigation = useCallback((result: VoiceCommandResult): boolean => {
    if (result.data?.expensesPageAction) {
      window.dispatchEvent(new CustomEvent('voiceExpensesAction', { detail: result.data.expensesPageAction }));
    }

    if (result.data?.contactsPageAction) {
      window.dispatchEvent(new CustomEvent('voiceContactsAction', { detail: result.data.contactsPageAction }));
    }

    if (result.data?.chartOfAccountsPageAction) {
      window.dispatchEvent(new CustomEvent('voiceChartOfAccountsAction', { detail: result.data.chartOfAccountsPageAction }));
    }

    if (result.data?.bankAccountsPageAction) {
      window.dispatchEvent(new CustomEvent('voiceBankAccountsAction', { detail: result.data.bankAccountsPageAction }));
    }

    if (result.data?.invoicesPageAction) {
      window.dispatchEvent(new CustomEvent('voiceInvoicesAction', { detail: result.data.invoicesPageAction }));
    }

    if (result.data?.quotesPageAction) {
      window.dispatchEvent(new CustomEvent('voiceQuotesAction', { detail: result.data.quotesPageAction }));
    }

    if (result.data?.inventoryPageAction) {
      window.dispatchEvent(new CustomEvent('voiceInventoryAction', { detail: result.data.inventoryPageAction }));
    }

    if (result.data?.taxPeriodsPageAction) {
      window.dispatchEvent(new CustomEvent('voiceTaxPeriodsAction', { detail: result.data.taxPeriodsPageAction }));
    }

    if (result.data?.reportsPageAction) {
      window.dispatchEvent(new CustomEvent('voiceReportsAction', { detail: result.data.reportsPageAction }));
    }

    if (result.data?.imageProcessingPageAction) {
      window.dispatchEvent(new CustomEvent('voiceImageProcessingAction', { detail: result.data.imageProcessingPageAction }));
    }

    if (result.data?.dashboardPageAction) {
      dispatchVoiceDashboardAction(result.data.dashboardPageAction);
    }

    const detailRoute = resolveVoiceDetailRoute(result);
    if (detailRoute) {
      maybeCollapseRailOnVoiceNavigate(detailRoute, setRailOpen);
      appendRouteNavigation(detailRoute);
      setTimeout(() => closeAssistant(), 1000);
      return true;
    }

    const isClarifying =
      result.action === 'clarify' ||
      result.phase === 'gathering_data' ||
      result.phase === 'gathering_quote' ||
      result.data?.phase === 'gathering_quote' ||
      result.phase === 'clarification' ||
      result.phase === 'confirmation' ||
      result.phase === 'contact_confirmation' ||
      result.needsConfirmation === true;

    if (!isClarifying) {
      const destinationFromMessage = getDestinationFromMessage(result.message);
      if (destinationFromMessage) {
        maybeCollapseRailOnVoiceNavigate(destinationFromMessage, setRailOpen);
        executeNavigation(destinationFromMessage);
        setTimeout(() => closeAssistant(), 1000);
        return true;
      }
    }

    if (result.action === 'navigate' && result.data?.route) {
      maybeCollapseRailOnVoiceNavigate(result.data.route, setRailOpen);
      appendRouteNavigation(result.data.route);
      setTimeout(() => closeAssistant(), 1000);
      return true;
    }

    if (result.action === 'navigate' && result.data?.destination) {
      const route = routeFromDestination(result.data.destination);
      maybeCollapseRailOnVoiceNavigate(route, setRailOpen);
      appendRouteNavigation(route);
      setTimeout(() => closeAssistant(), 1000);
      return true;
    }

    return false;
  }, [closeAssistant, setRailOpen]);

  const handleVoiceCommandResult = useCallback((result: VoiceCommandResult, sessionId: string) => {
    storeSharedMind(result);
    scheduleProactiveMessages(result);

    const proactiveSuggestions = result.shared_mind?.proactive_suggestions;
    if (Array.isArray(proactiveSuggestions) && proactiveSuggestions.length > 0) {
      const queries = proactiveSuggestions
        .map((item: { text?: string }) => item?.text?.trim())
        .filter((text): text is string => Boolean(text))
        .slice(0, 4);
      setDynamicSuggestedQueries(queries);
    } else if (Array.isArray(result.suggestions) && result.suggestions.length > 0) {
      const queries = result.suggestions
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
        .slice(0, 4);
      setDynamicSuggestedQueries(queries);
    }

    if (result.session_id) {
      localStorage.setItem('voiceSessionId', result.session_id);
    } else {
      localStorage.setItem('voiceSessionId', sessionId);
    }

    if (result.phase === 'contact_confirmation') {
      storePendingContactConfirmation(result);
    } else {
      clearPendingContactConfirmation();
    }

    if (
      result.phase === 'gathering_items'
      || result.data?.gathering_items === true
      || result.data?.missing_entity?.startsWith('item_')
      || result.data?.missing_entity === 'more_items'
    ) {
      storePendingDocumentFlow(result);
    } else if (shouldCloseVoiceAssistant(result) || result.phase === 'cancelled') {
      clearPendingDocumentFlow();
    }

    if (isFeedbackBlocked(result)) return;

    const spokenMessage = result.message || (result.success ? 'Done.' : 'Sorry, I could not complete that. Please try again.');
    const displayMessage = result.data?.full_message || spokenMessage;

    const assistantMessage = appendAssistantMessage(
      displayMessage,
      getVoiceMessageExtras(result)
    );
    speakResponse(spokenMessage);

    if (
      result.phase === 'gathering_data'
      || result.phase === 'gathering_quote'
      || result.phase === 'gathering_items'
      || result.data?.phase === 'gathering_quote'
      || result.data?.gathering_items === true
      || result.needsConfirmation
    ) {
      trackAssistantQuestion(
        result.message || result.nextQuestion || result.missingFields?.[0] || '',
        result.nextQuestion || result.missingFields?.[0]
      );
    }

    if (
      result.phase === 'gathering_customer' ||
      result.data?.phase === 'gathering_customer' ||
      result.phase === 'gathering_quote' ||
      result.data?.phase === 'gathering_quote' ||
      (result.data?.suggested_customers && Array.isArray(result.data.suggested_customers)) ||
      (result.data?.suggested_suppliers && Array.isArray(result.data.suggested_suppliers))
    ) {
      setQuickPicks(extractVoiceQuickPicks(result));
    } else {
      setQuickPicks([]);
    }

    updateConfirmCard(result);

    const navigated = handleResultNavigation(result);

    const establishedIntent = extractEstablishedIntent(result);
    if (!navigated && establishedIntent) {
      if (activeIntentRef.current !== establishedIntent) {
        navigatedForIntentRef.current = null;
        activeIntentRef.current = establishedIntent;
      }
      if (navigatedForIntentRef.current !== establishedIntent) {
        if (navigateForEstablishedIntent(establishedIntent)) {
          navigatedForIntentRef.current = establishedIntent;
        }
      }
    }

    if (shouldCloseVoiceAssistant(result)) {
      setQuickPicks([]);
      setTimeout(() => closeAssistant(), 1500);
    } else if (!navigated && result.action !== 'navigate') {
      if (isIntentFullyExecuted(result) && !needsContinuation(result)) {
        clearLoopGuard();
      } else if (needsContinuation(result)) {
        setIsOpen(true);
      }
    }

    if (result.data?.component) {
      setActiveComponent(result.data.component);
      setComponentData(result.data.componentData);
    }

    if (shouldDispatchVoiceDataRefresh(result)) {
      pendingDataRefreshRef.current = result;
      dispatchVoiceDataRefresh(result);
      maybeRefreshDashboardAfterMutation(result.action);
    }
  }, [appendAssistantMessage, closeAssistant, handleResultNavigation, scheduleProactiveMessages, updateConfirmCard]);

  useEffect(() => {
    dashboardGreetingInjectedRef.current = false;

    setMessages((prev) => {
      const next = prev.filter(
        (msg) => !(msg.role === 'assistant' && isDashboardProactiveGreeting(msg.content))
      );
      if (next.length !== prev.length) {
        persistMessages(next);
        return next;
      }
      return prev;
    });
  }, [location.pathname, persistMessages]);

  useEffect(() => {
    if (!isDashboardPath(location.pathname)) {
      return;
    }

    const assistantVisible = isDesktop ? railOpen : isOpen;
    // Desktop rail uses VoiceAssistantRailEmptyState for welcome content.
    if (isDesktop && railOpen) return;
    if (!assistantVisible || messages.length > 0 || dashboardGreetingInjectedRef.current) return;

    dashboardGreetingInjectedRef.current = true;
    appendAssistantMessage(getDashboardProactiveGreeting(i18n.language));
  }, [appendAssistantMessage, i18n.language, isDesktop, isOpen, location.pathname, messages.length, railOpen]);

  const handleTranscription = useCallback(async (text: string) => {
    const trimmed = text.trim();
    const isNumericQuickPick = /^\d+$/.test(trimmed);
    if (!trimmed || (trimmed.length < 2 && !isNumericQuickPick)) {
      setIsProcessing(false);
      return;
    }

    log.voice.transcription(trimmed);

    if (isLoopDetected()) {
      appendAssistantMessage(getLoopRecoveryMessage());
      clearLoopGuard();
      setIsProcessing(false);
      return;
    }

    setMessages(prev => [...prev, toVoiceMessage('user', trimmed)]);
    setIsProcessing(true);
    setCurrentTranscript('');

    try {
      const dashboardRefresh = tryDashboardRefreshFastTrack(trimmed);
      if (dashboardRefresh) {
        appendAssistantMessage(dashboardRefresh.message);
        return;
      }

      const suggestedQuery = tryDashboardSuggestedQueryFastTrack(trimmed, setRailOpen);
      if (suggestedQuery) {
        appendAssistantMessage(suggestedQuery.message);
        return;
      }
      if (await tryClientFastTracks(trimmed)) return;
      if (handleLocalNavigation(trimmed)) return;
      if (await handleServerFastNavigation(trimmed)) return;

      const sessionId = ensureVoiceSessionId();
      const result = await sendVoiceCommand(trimmed, sessionId, {
        isOpen,
        isMinimized,
        isRecording,
        messages,
        assistantContext: (window as any).__GVA_STATE__?.assistantContext,
      });
      log.voice.response(result);
      handleVoiceCommandResult(result, sessionId);
    } catch (error) {
      log.error?.('Voice command error:', error);
      appendAssistantMessage(error instanceof Error ? error.message : 'Sorry, I encountered an error processing your request.');
    } finally {
      setIsProcessing(false);
      setIsRecording(false);
      setCurrentTranscript('');
    }
  }, [appendAssistantMessage, handleLocalNavigation, handleServerFastNavigation, handleVoiceCommandResult, isMinimized, isOpen, isRecording, messages, toVoiceMessage, tryClientFastTracks]);

  const handleProcessingChange = useCallback((processing: boolean) => setIsProcessing(processing), []);
  const handleOpenHistory = useCallback((show?: boolean) => {
    setPastSessions(getAllSessions());
    setShowHistoryModal(show !== undefined ? show : true);
  }, []);
  const handleCloseHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
    setSelectedPastSession(null);
  }, []);
  const handleSelectPastSession = useCallback((session: VoiceSession) => setSelectedPastSession(session), []);

  const handleLoadSession = useCallback((session: VoiceSession) => {
    if (!session?.id) return;
    const loaded = loadSession(session.id);
    if (!loaded) return;
    sessionIdRef.current = loaded.id;
    setMessages(getSessionMessages(loaded.id));
    setSelectedPastSession(loaded);
    setShowHistoryModal(false);
    clearLoopGuard();
  }, []);

  const handleLoadSelectedSession = useCallback(() => {
    if (!selectedPastSession) return;
    handleLoadSession(selectedPastSession);
  }, [handleLoadSession, selectedPastSession]);

  const handleStartNewChat = useCallback(() => {
    beginNewChatSession();
  }, [beginNewChatSession]);

  const handleClearHistory = useCallback(() => {
    clearAllSessions();
    const session = startNewSession(sessionOrigin());
    sessionIdRef.current = session.id;
    notifyVoiceSessionChanged();
    setPastSessions([]);
    setMessages([]);
    setSelectedPastSession(null);
    localStorage.removeItem('voiceSessionId');
    clearPendingContactConfirmation();
    clearPendingDocumentFlow();
  }, [sessionOrigin]);

  const handleCloseComponent = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const handleConfirmTransaction = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const handleConfirmQuotation = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const setupTranscriptListener = useCallback(() => {
    const handler = (event: CustomEvent) => setCurrentTranscript(event.detail.transcript);
    window.addEventListener('transcriptUpdate', handler as EventListener);
    return () => window.removeEventListener('transcriptUpdate', handler as EventListener);
  }, []);

  const setupRecordingListener = useCallback(() => {
    const handler = (event: CustomEvent) => setIsRecording(event.detail);
    window.addEventListener('recordingStateChange', handler as EventListener);
    return () => window.removeEventListener('recordingStateChange', handler as EventListener);
  }, []);

  const handleFileUpload = useCallback(async (file: File) => {
    setMessages(prev => [...prev, toVoiceMessage('user', `Uploaded file: ${file.name}`)]);
    setIsProcessing(true);

    try {
      const token = localStorage.getItem('token');
      const businessIdStr = localStorage.getItem('businessId');
      const businessId = businessIdStr ? parseInt(businessIdStr, 10) : undefined;
      if (!token || !businessId) throw new Error('Not authenticated or no business selected');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', businessId.toString());

      const response = await fetch(`${API_BASE_URL}/voice/process-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      appendAssistantMessage(result.message || `Successfully processed ${result.data?.processed || 0} records from ${file.name}`);
      if (result.data?.summary) appendAssistantMessage(`Summary: ${result.data.summary}`);
    } catch (error) {
      appendAssistantMessage(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  }, [appendAssistantMessage, toVoiceMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const file = event.detail?.file;
      if (file) handleFileUpload(file);
    };
    window.addEventListener('voiceAssistantFileUpload', handler as EventListener);
    return () => window.removeEventListener('voiceAssistantFileUpload', handler as EventListener);
  }, [handleFileUpload]);

  useEffect(() => {
    const handler = () => {
      appendAssistantMessage(i18n.t('dashboard.assistant.dashboardUpdated', 'Dashboard updated'));
    };

    window.addEventListener('voiceDashboardRefreshComplete', handler as EventListener);
    return () => window.removeEventListener('voiceDashboardRefreshComplete', handler as EventListener);
  }, [appendAssistantMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const message = event.detail?.message;
      if (message) appendAssistantMessage(message);
    };
    window.addEventListener('voiceMicError', handler as EventListener);
    return () => window.removeEventListener('voiceMicError', handler as EventListener);
  }, [appendAssistantMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const text = event.detail?.text;
      if (text) handleTranscription(text);
    };
    window.addEventListener('voiceConfirmAction', handler as EventListener);
    return () => window.removeEventListener('voiceConfirmAction', handler as EventListener);
  }, [handleTranscription]);

  const handleQuickPickSelect = useCallback((option: VoiceQuickPickOption) => {
    const text = option.quote_number || option.name || String(option.index);
    void handleTranscription(text);
  }, [handleTranscription]);

  return {
    isOpen,
    isMinimized,
    railOpen,
    displayMode,
    toggleRail,
    messages,
    isRecording,
    isProcessing,
    currentTranscript,
    showHistoryModal,
    pastSessions,
    selectedPastSession,
    activeComponent,
    componentData,
    confirmCard,
    quickPicks,
    dynamicSuggestedQueries,
    pageSuggestedQueries,
    handleQuickPickSelect,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    maximizeAssistant,
    startVoiceInput,
    stopVoiceInput,
    handleTranscription,
    handleProcessingChange,
    handleOpenHistory,
    handleCloseHistoryModal,
    handleSelectPastSession,
    handleLoadSession,
    handleLoadSelectedSession,
    handleStartNewChat,
    handleClearHistory,
    handleCloseComponent,
    handleConfirmTransaction,
    handleConfirmQuotation,
    setupTranscriptListener,
    setupRecordingListener
  };
};
